import { Box, Button, Flex, SortButton } from '@gmg/gmg-react-components';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatter from 'src/customHooks/useFormatter';
import useSort from 'src/customHooks/useSort';
import { SortingOrder, Table, TableContainer, TableContainerCardStyle, Thead, Tr } from 'src/shared/TableStyle';
import WrappedSpinner from 'src/shared/WrappedSpinner';
import { ActivityViewModel } from './activityViewModel';

type ActivityTableProps = {
    rows: ActivityViewModel[];
    isInitialLoading: boolean;
    isLoadingMore: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    filterValue: string;
    showTargetOrganizationColumn?: boolean;
};

const ActivityTable: FC<ActivityTableProps> = ({
    rows, isInitialLoading, isLoadingMore, hasNextPage, onLoadMore, filterValue, showTargetOrganizationColumn = false
}) => {
    const { t } = useTranslation();
    const { formatTime, formatDate } = useFormatter();
    const [sortingStatus, setSortingStatus] = useState<SortingOrder>({ direction: 'DESC', propertyName: 'date' });
    const { getSortFn } = useSort();
    const lowerCaseFilterValue = filterValue.trim().toLowerCase();
    const sortFn = getSortFn(sortingStatus.propertyName, sortingStatus.direction);

    const filteredRows = lowerCaseFilterValue
        ? rows.filter(({ performedBy, performedByEmail, targetOrganizationName, activity, date }) => {
            const fields = [performedBy, performedByEmail, targetOrganizationName, activity, date];
            return fields.some((field) => field.toLowerCase().includes(lowerCaseFilterValue));
        })
        : rows;
    const sortedAndFilteredRows = [...filteredRows].sort(sortFn);

    const handleSortButtonClick = (propertyName: string) => {
        setSortingStatus((prev) => ({
            direction: prev.propertyName === propertyName && prev.direction === 'ASC' ? 'DESC' : 'ASC',
            propertyName,
        }));
    };

    return (
        <TableContainer>
            {isInitialLoading
                ? <WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />
                : <TableContainerCardStyle aria-label='TableContainerCardStyle' className="vertical-scrollbar">
                    <Table aria-label="activity-table">
                        <colgroup>
                            <col style={{ width: '320px' }} />
                            {showTargetOrganizationColumn && <col style={{ width: '220px' }} />}
                            <col style={{ width: 'auto' }} />
                            <col style={{ width: '320px' }} />
                        </colgroup>
                        <Thead>
                            <Tr isSelected={false} isHeaderRow>
                                <th className="px12_600_normal">
                                    <SortButton
                                        buttonLabel={t('ActivityManagement.tableLabels.performed_by_title', 'Performed by')}
                                        onClick={() => handleSortButtonClick('performedBy')}
                                        direction={sortingStatus.propertyName === 'performedBy' ? sortingStatus.direction : undefined}
                                    />
                                </th>
                                {showTargetOrganizationColumn &&
                                    <th className="px12_600_normal">
                                        <SortButton
                                            buttonLabel={t('ActivityManagement.tableLabels.organization_title', 'Organization')}
                                            onClick={() => handleSortButtonClick('targetOrganizationName')}
                                            direction={sortingStatus.propertyName === 'targetOrganizationName' ? sortingStatus.direction : undefined}
                                        />
                                    </th>
                                }
                                <th className="px12_600_normal">
                                    <SortButton
                                        buttonLabel={t('ActivityManagement.tableLabels.activity_title', 'Activity')}
                                        onClick={() => handleSortButtonClick('activity')}
                                        direction={sortingStatus.propertyName === 'activity' ? sortingStatus.direction : undefined}
                                    />
                                </th>
                                <th className="px12_600_normal">
                                    <SortButton
                                        buttonLabel={t('ActivityManagement.tableLabels.date_title', 'Date')}
                                        onClick={() => handleSortButtonClick('date')}
                                        direction={sortingStatus.propertyName === 'date' ? sortingStatus.direction : undefined}
                                    />
                                </th>
                            </Tr>
                        </Thead>
                        <tbody>
                            {sortedAndFilteredRows.map((row) => (
                                <Tr key={row.id} data-testid="row" isSelected={false}>
                                    <td className="px12_400_normal">
                                        <Flex gap='5px' flexDirection='column'>
                                            {row.performedBy !== '-' && <Box>{row.performedBy}</Box>}
                                            {row.performedByEmail !== '-' && <span className="px10_400_weak">{row.performedByEmail}</span>}
                                        </Flex>
                                    </td>
                                    {showTargetOrganizationColumn && <td className="px12_400_normal">{row.targetOrganizationName}</td>}
                                    <td className="px12_400_normal">{row.activity}</td>
                                    <td className="px12_400_normal">
                                        <Flex gap='5px' flexDirection='column'>
                                            <Box>{formatDate(row.date)}</Box>
                                            <span className="px10_400_weak">{formatTime(row.date)}</span>
                                        </Flex>
                                    </td>
                                </Tr>
                            ))}
                            {hasNextPage &&
                                <Tr isSelected={false}>
                                    <td colSpan={showTargetOrganizationColumn ? 4 : 3}>
                                        <Button
                                            disabled={isLoadingMore}
                                            variant="secondary"
                                            onClick={onLoadMore}
                                        >
                                            {isLoadingMore
                                                ? t('Common.Spinner', 'Loading...')
                                                : t('Common.LoadMore', 'Load more')}
                                        </Button>
                                    </td>
                                </Tr>
                            }
                        </tbody>
                    </Table>
                </TableContainerCardStyle>
            }
        </TableContainer>
    );
};

export default ActivityTable;
